import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building,
  MessageSquare,
  File,
  Settings,
  LogOut,
  Menu,
  Send,
  FileText,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { cn } from "@/lib/utils";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onLogout: () => void;
}

const Sidebar = ({ isCollapsed, onToggle, onLogout }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, isLoading } = useCurrentUser();

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: "Dashboard",
      path: "/dashboard",
    },
    {
      icon: Building,
      label: "Empresas",
      path: "/empresas",
    },
    {
      icon: Users,
      label: "Usuários",
      path: "/usuarios",
    },
    {
      icon: File,
      label: "Documentos",
      path: "/documentos",
    },
    {
      icon: MessageSquare,
      label: "Chat",
      path: "/chat",
    },
    {
      icon: Send,
      label: "Campanhas",
      path: "/campanhas",
      children: [
        {
          icon: Users,
          label: "Leads",
          path: "/leads",
        },
        {
          icon: FileText,
          label: "Templates",
          path: "/templates",
        },
      ],
    },
    // Remover Configurações daqui
    {
      icon: Info,
      label: "Sobre",
      path: "/about",
    },
  ];

  // Item de configurações separado
  const settingsItem = {
    icon: Settings,
    label: "Configurações",
    path: "/configuracoes",
  };

  return (
    <div
      className={cn(
        "bg-background border-r border-border transition-all duration-300 flex flex-col h-full",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex-1 mr-2">
              <h2 className="text-xl font-bold text-foreground truncate">
                {isLoading ? "Carregando..." : currentUser.company || "Sistema"}
              </h2>
              {currentUser.company &&
                currentUser.company !== "Sistema" &&
                !isLoading && (
                  <p className="text-xs text-muted-foreground truncate">
                    {currentUser.name}
                  </p>
                )}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="h-8 w-8 p-0 flex-shrink-0"
          >
            <Menu className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.path ||
              (item.children &&
                item.children.some(
                  (child) => location.pathname === child.path
                ));

            return (
              <li key={item.path}>
                <button
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "w-full flex items-center px-3 py-2 rounded-lg transition-colors text-left",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {!isCollapsed && (
                    <span className="ml-3 truncate">{item.label}</span>
                  )}
                </button>
                {/* Submenus */}
                {!isCollapsed && item.children && (
                  <ul className="ml-8 mt-1 space-y-1">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const isChildActive = location.pathname === child.path;
                      return (
                        <li key={child.path}>
                          <button
                            onClick={() => navigate(child.path)}
                            className={cn(
                              "w-full flex items-center px-2 py-1 rounded transition-colors text-left text-sm",
                              isChildActive
                                ? "bg-primary/20 text-primary font-semibold"
                                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                            )}
                          >
                            <ChildIcon className="h-4 w-4 flex-shrink-0" />
                            <span className="ml-2 truncate">{child.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Configurações no rodapé */}
      <div className="p-4 border-t border-border mt-auto">
        <button
          onClick={() => navigate(settingsItem.path)}
          className={cn(
            "w-full flex items-center px-3 py-2 rounded-lg transition-colors text-left mb-2",
            location.pathname === settingsItem.path
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          )}
        >
          <settingsItem.icon className="h-5 w-5 flex-shrink-0" />
          {!isCollapsed && (
            <span className="ml-3 truncate">{settingsItem.label}</span>
          )}
        </button>
        <button
          onClick={onLogout}
          className="w-full flex items-center px-3 py-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!isCollapsed && <span className="ml-3">Sair</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
